import React, { useMemo } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { useTheme } from '../../../theme/useTheme';
import { purchaseProcessingOverlayStyles } from './PurchaseProcessingOverlay.styles';

interface PurchaseProcessingOverlayProps {
  visible: boolean;
  message: string;
  /**
   * When provided, renders an escape-hatch button. Wired to the watchdog only —
   * a normal purchase clears itself, so this is the backstop for a stuck spinner.
   */
  onDismiss?: () => void;
  dismissLabel?: string;
}

/**
 * Full-screen, touch-capturing overlay shown while a purchase is in flight. It
 * blocks every other control in the shop (other bundles, tab switch, close,
 * daily reward) so a second charge can't be triggered mid-transaction.
 */
export const PurchaseProcessingOverlay: React.FC<PurchaseProcessingOverlayProps> = ({
  visible, message, onDismiss, dismissLabel,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => purchaseProcessingOverlayStyles(theme), [theme]);

  if (!visible) return null;

  return (
    <Pressable
      style={styles.backdrop}
      // Swallow every tap so nothing behind the overlay can be pressed.
      onPress={() => undefined}
      accessibilityViewIsModal
      accessibilityRole="progressbar"
      accessibilityLabel={message}
    >
      <View style={styles.card}>
        <ActivityIndicator size="large" color={theme.accent} />
        <Text style={styles.message}>{message}</Text>
        {onDismiss != null && (
          <Pressable
            onPress={onDismiss}
            style={({ pressed }) => [styles.dismissBtn, pressed && styles.dismissBtnPressed]}
            accessibilityRole="button"
            accessibilityLabel={dismissLabel ?? 'Dismiss'}
          >
            <Text style={styles.dismissText}>{dismissLabel ?? 'Dismiss'}</Text>
          </Pressable>
        )}
      </View>
    </Pressable>
  );
};
